import { pick } from 'lodash-es';
import SmartyStreetsSDK from 'smartystreets-javascript-sdk';
import gql from 'graphql-tag';
import { branchLiveStates } from '@ourbranch/lookups';
import * as Sentry from '@sentry/react';

import { apolloClient } from 'core/apollo-client';
import { track } from 'core/helpers/analytics';
import config from '../../aws-exports';

const Core = SmartyStreetsSDK.core;
const AutocompleteLookup = SmartyStreetsSDK.usAutocompletePro.Lookup;
const StreetLookup = SmartyStreetsSDK.usStreet.Lookup;
const credentials = new Core.SharedCredentials(config.smartyStreetsKey);
const PROXY_FALLBACK_STORAGE_KEY = 'br.address-suggest.use-proxy-fallback';

const ADDRESS_AUTOCOMPLETE = gql`
  query staffAddressAutocomplete(
    $search: String!
    $selected: String
    $maxResults: Int
    $includeOnlyStates: [String!]
    $preferStates: [String!]
    $preferGeolocation: String
  ) {
    addressAutocomplete(
      search: $search
      selected: $selected
      maxResults: $maxResults
      includeOnlyStates: $includeOnlyStates
      preferStates: $preferStates
      preferGeolocation: $preferGeolocation
    ) {
      streetLine
      secondary
      city
      state
      zipcode
      entries
      source
    }
  }
`;

let inMemoryProxySticky = false;

export const resetAddressAutocompleteProxyFallbackForTests = () => {
  inMemoryProxySticky = false;

  try {
    window.sessionStorage.removeItem(PROXY_FALLBACK_STORAGE_KEY);
  } catch (_error) {
    // Ignore storage failures in tests that replace sessionStorage.
  }
};

const isProxySticky = () => {
  if (inMemoryProxySticky) {
    return true;
  }

  try {
    return window.sessionStorage.getItem(PROXY_FALLBACK_STORAGE_KEY) === '1';
  } catch (_error) {
    return inMemoryProxySticky;
  }
};

const markProxySticky = () => {
  inMemoryProxySticky = true;

  try {
    window.sessionStorage.setItem(PROXY_FALLBACK_STORAGE_KEY, '1');
  } catch (_error) {
    // Some browser modes expose sessionStorage but throw on access.
  }
};

const mapSuggestions = (suggestions = []) =>
  suggestions.map((result) => ({
    ...pick(result, ['city', 'entries', 'secondary', 'state', 'streetLine', 'zipcode']),
    secondary: result.secondary ?? '',
    entries: result.entries ?? 0
  }));

const trackProxyFallback = (trigger, input) => {
  try {
    track('Address Search Proxy Fallback', {
      trigger,
      searchLength: input?.length ?? 0,
      page: window.location.pathname
    });
  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

// Smarty rejects a longer `search` outright ("The search value must be between 1 and 127 bytes.").
// Sliced by character rather than byte: US address input is ASCII, so the two agree in practice.
const MAX_SMARTY_SEARCH_LENGTH = 127;

// Smarty answers a malformed request with one of these whoever asks, so the AppSync proxy would
// fail exactly the same way — let those through to the caller instead of retrying.
const MALFORMED_REQUEST_STATUSES = [400, 413, 422];

// Everything else is about this browser's embedded key or its connection, and the proxy is subject
// to none of it: it holds the secret auth-id/token server side. That covers the 401 referer/host
// restriction, the 402 subscription check, the 403 Smarty returns for an embedded key used from a
// public cloud IP, the 429 per-IP throttle NAT'd agency offices share and hit, and a rejection with
// no status at all — which is what the SDK produces whenever axios itself threw (a 5xx, a timeout,
// a request an extension blocked), because HttpSender drops the axios error and StatusCodeSender
// overwrites it with a generic "unexpected error".
//
// Listing what to give up on rather than what to retry is deliberate: an allow-list of 401/429 let
// the 403 through untouched, and we only know the 403 isn't a 401 because the pre-#32647 code
// already caught 401s and returned proxy results, so a 401 could never have reached Sentry at all.
const proxyFallbackTrigger = (error) => {
  const statusCode = error?.statusCode ?? error?.status;

  if (statusCode === undefined) {
    return 'network';
  }

  return MALFORMED_REQUEST_STATUSES.includes(statusCode) ? null : String(statusCode);
};

const getProxySuggestions = async (value, allowedStates) => {
  const { data } = await apolloClient.query({
    query: ADDRESS_AUTOCOMPLETE,
    variables: {
      search: value,
      selected: null,
      maxResults: 5,
      includeOnlyStates: allowedStates?.length ? allowedStates : null,
      preferStates: branchLiveStates,
      preferGeolocation: 'city'
    },
    fetchPolicy: 'no-cache'
  });

  return mapSuggestions(data?.addressAutocomplete ?? []);
};

export default {
  client: Core.buildClient.usAutocompletePro(credentials),
  addressValidatorClient: Core.buildClient.usStreet(credentials),

  async search(value, allowedStates) {
    const search = String(value ?? '').slice(0, MAX_SMARTY_SEARCH_LENGTH);

    if (isProxySticky()) {
      trackProxyFallback('sticky', search);
      return getProxySuggestions(search, allowedStates);
    }

    const lookup = new AutocompleteLookup(search);
    lookup.maxResults = 5;
    // Only set includeOnlyStates if we have states to filter by
    // When allowedStates is undefined/null, allow all US states (no filter)
    if (allowedStates?.length) {
      lookup.includeOnlyStates = allowedStates;
    }
    lookup.prefer = branchLiveStates;
    lookup.geolocate = true;
    lookup.geolocatePrecision = 'CITY';

    // Only the Smarty call itself is classified. Mapping the results below is our own code, and a
    // bug in it has no status code, so leaving it inside the try would read as a network failure:
    // it would flip the tab to the proxy for the rest of the session and never reach Sentry.
    let results;

    try {
      ({ result: results } = await this.client.send(lookup));
    } catch (error) {
      const trigger = proxyFallbackTrigger(error);

      if (!trigger) {
        throw error;
      }

      trackProxyFallback(trigger, search);
      const proxySuggestions = await getProxySuggestions(search, allowedStates);

      // Stick to the proxy only once it has actually answered. Part of the no-status bucket is a
      // Smarty 5xx or outage, which the proxy hits too because it calls the same API — pinning the
      // session to an equally broken path would leave autocomplete dead for the whole tab even
      // after the direct key recovers. Sticking on success keeps the cases the proxy really does
      // fix (a throttled office IP, an extension blocking smarty.com) off Smarty for the session.
      markProxySticky();
      return proxySuggestions;
    }

    return mapSuggestions(results);
  },

  async validateAddress(addressIn) {
    let smartyResponse;
    try {
      const { address, address2, city, state, zip } = addressIn;
      const lookup = new StreetLookup();
      lookup.street = address;
      lookup.secondary = address2;
      lookup.city = city;
      lookup.state = state;
      lookup.zipCode = zip;
      lookup.match = 'strict';

      smartyResponse = await this.addressValidatorClient.send(lookup);

      // on autocomplete selection, our code fills out 'Apt' in the address2 field upon selection
      // so if it's just 'Apt' then it's missing the unit number
      const missingAddress2 = !address2 || !address2?.trim() || address2?.trim() === 'Apt';

      // Check if address is missing unit number via Delivery Point Validation (DPV) footnotes
      // N1 — Address is missing secondary information (apartment, suite, etc.) which IS REQUIRED for delivery
      // https://smartystreets.com/docs/cloud/us-street-api#dpv-footnotes
      const needsUnitNumber =
        missingAddress2 && smartyResponse.lookups?.[0]?.result?.[0]?.analysis?.dpvFootnotes?.includes('N1');

      return {
        results: smartyResponse.lookups,
        needsUnitNumber
      };
    } catch (e) {
      // The SDK collapses every failure into one generic `Error: unexpected error`: StatusCodeSender
      // overwrites the rejected Response's `error` with its own, and only fills in a real message
      // when Smarty answered with a JSON body. A throttle, a Smarty 5xx and a request an extension
      // blocked all arrive in Sentry as the same undiagnosable issue (#32647). The rejected value is
      // the SDK's Response, so tag its status — `none` means the request never got a reply.
      Sentry.captureException(e, {
        tags: {
          smartyStatusCode: e?.statusCode ?? 'none'
        },
        extra: {
          addressIn,
          smartyResponse,
          smartyPayload: e?.payload,
          failedToValidateAddress: true
        }
      });
      return {
        results: [],
        needsUnitNumber: false
      };
    }
  }
};
